import { ArrowRight } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Field } from '@/shared/ui/field';
import { Input } from '@/shared/ui/input';

import { fieldErrors, signUpSchema, type SignUpInput } from '../auth.schemas';

import { PasswordInput } from './password-input';

export interface SignUpFormProps {
  onSubmit: (input: SignUpInput) => Promise<string | null>;
}

export function SignUpForm({ onSubmit }: SignUpFormProps) {
  const [values, setValues] = useState<SignUpInput>({ displayName: '', email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      form.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus();
    }
  }, [errors]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    const parsed = signUpSchema.safeParse(values);
    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setFormError(null);
    setSubmitting(true);
    try {
      setFormError(await onSubmit(parsed.data));
    } catch {
      setFormError('Could not connect. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form ref={form} onSubmit={handleSubmit} noValidate className="auth-form">
      {formError ? <Alert>{formError}</Alert> : null}
      <Field label="Name" htmlFor="displayName" error={errors['displayName']}>
        <Input
          id="displayName"
          autoComplete="name"
          placeholder="Your full name"
          value={values.displayName}
          invalid={Boolean(errors['displayName'])}
          aria-describedby={errors['displayName'] ? 'displayName-description' : undefined}
          onChange={(e) => setValues((v) => ({ ...v, displayName: e.target.value }))}
        />
      </Field>
      <Field label="Email" htmlFor="email" error={errors['email']}>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={values.email}
          invalid={Boolean(errors['email'])}
          aria-describedby={errors['email'] ? 'email-description' : undefined}
          onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
        />
      </Field>
      <Field
        label="Password"
        htmlFor="password"
        error={errors['password']}
        hint="At least 8 characters"
      >
        <PasswordInput
          id="password"
          autoComplete="new-password"
          placeholder="Create a password"
          value={values.password}
          invalid={Boolean(errors['password'])}
          aria-describedby="password-description"
          onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
        />
      </Field>
      <Button
        type="submit"
        className="mt-1 w-full"
        loading={submitting}
        loadingLabel="Creating your account…"
      >
        Create your account <ArrowRight className="size-4" aria-hidden="true" />
      </Button>
    </form>
  );
}
